import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  BarController,
  CategoryScale,
  DoughnutController,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import "./OrdersCharts.css";

ChartJS.register(
  ArcElement,
  BarElement,
  BarController,
  CategoryScale,
  DoughnutController,
  Legend,
  LinearScale,
  Title,
  Tooltip
);

const DOUGHNUT_COLORS = [
  "#977644",
  "#0891b2",
  "#16a34a",
  "#ea580c",
  "#7c3aed",
  "#64748b",
];

/**
 * @param {{
 *   categoryCounts?: { label: string; count: number }[] | null;
 *   trendLabels: string[];
 *   trendValues: number[];
 *   trendRange: string;
 *   onTrendRangeChange: (v: string) => void;
 *   customFromDate: string;
 *   customToDate: string;
 *   onCustomFromDateChange: (v: string) => void;
 *   onCustomToDateChange: (v: string) => void;
 *   trendRangeOptions: { value: string; label: string }[];
 *   trendSubtitle: string;
 *   trendLoading?: boolean;
 *   chartRefreshKey?: string;
 *   onViewOrders: () => void;
 * }} props
 */
export default function OrdersCharts({
  categoryCounts,
  trendLabels,
  trendValues,
  trendRange,
  onTrendRangeChange,
  customFromDate,
  customToDate,
  onCustomFromDateChange,
  onCustomToDateChange,
  trendRangeOptions,
  trendSubtitle,
  trendLoading = false,
  chartRefreshKey = "trend",
  onViewOrders,
}) {
  const hasCategory = Array.isArray(categoryCounts) && categoryCounts.length > 0;

  const pointCount = Array.isArray(trendLabels) ? trendLabels.length : 0;

  const barLayout = useMemo(() => {
    if (pointCount <= 7) {
      return {
        maxBarThickness: 56,
        categoryPercentage: 0.72,
        barPercentage: 0.78,
        borderRadius: { topLeft: 10, topRight: 10, bottomLeft: 2, bottomRight: 2 },
      };
    }
    if (pointCount <= 31) {
      const maxThickness = Math.max(18, Math.min(42, Math.floor(960 / Math.max(pointCount, 6))));
      return {
        maxBarThickness: maxThickness,
        categoryPercentage: 0.88,
        barPercentage: 0.88,
        borderRadius: { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 },
      };
    }
    return {
      maxBarThickness: 48,
      categoryPercentage: 0.65,
      barPercentage: 0.78,
      borderRadius: { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 },
    };
  }, [pointCount]);

  const doughnutData = useMemo(() => {
    if (!hasCategory) return null;
    return {
      labels: categoryCounts.map((c) => c.label),
      datasets: [
        {
          data: categoryCounts.map((c) => c.count),
          backgroundColor: categoryCounts.map(
            (_, i) => DOUGHNUT_COLORS[i % DOUGHNUT_COLORS.length]
          ),
          borderWidth: 2,
          borderColor: "#fff",
          hoverBorderColor: "#fff",
        },
      ],
    };
  }, [categoryCounts, hasCategory]);

  const doughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            padding: 14,
            usePointStyle: true,
          },
        },
        title: {
          display: !!hasCategory,
          text: "Orders by Type",
          font: { size: 16, weight: "600" },
          color: "#111827",
          padding: { bottom: 12 },
        },
      },
      cutout: "58%",
    }),
    [hasCategory]
  );

  const barData = useMemo(
    () => ({
      labels: trendLabels,
      datasets: [
        {
          label: "Orders",
          data: trendValues,
          backgroundColor: "rgba(217, 180, 113, 0.88)",
          hoverBackgroundColor: "rgba(176, 127, 60, 0.95)",
          borderColor: "rgba(154, 115, 64, 0.45)",
          borderWidth: 0,
          borderRadius: barLayout.borderRadius,
          borderSkipped: false,
          maxBarThickness: barLayout.maxBarThickness,
          categoryPercentage: barLayout.categoryPercentage,
          barPercentage: barLayout.barPercentage,
        },
      ],
    }),
    [trendLabels, trendValues, barLayout]
  );

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index",
        intersect: false,
      },
      animation: {
        duration: 420,
      },
      layout: {
        padding: { left: 8, right: 12, top: 16, bottom: 8 },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) =>
              items?.[0] ? `${items[0].label}` : "",
            label: (ctx) => {
              const n = Number(ctx.raw);
              return `Orders: ${Number.isFinite(n) ? n : 0}`;
            },
          },
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          cornerRadius: 10,
          padding: 12,
        },
      },
      scales: {
        x: {
          offset: true,
          grid: {
            display: pointCount > 7,
            drawBorder: false,
            tickLength: 0,
            color: "rgba(148, 163, 184, 0.12)",
          },
          ticks: {
            autoSkip: pointCount <= 34 ? false : true,
            maxTicksLimit: pointCount <= 34 ? pointCount + 2 : undefined,
            maxRotation: pointCount > 20 ? 70 : pointCount > 12 ? 50 : pointCount > 7 ? 40 : 0,
            minRotation: pointCount > 20 ? 65 : pointCount > 12 ? 40 : pointCount > 7 ? 32 : 0,
            padding: pointCount > 22 ? 2 : 4,
            color: "#64748b",
            font: {
              size: pointCount > 24 ? 9 : pointCount > 14 ? 10 : pointCount > 7 ? 11 : 12,
              weight: "500",
              family: "'Inter', system-ui, sans-serif",
            },
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(148, 163, 184, 0.18)",
          },
          ticks: {
            precision: 0,
            padding: 10,
            color: "#64748b",
            font: {
              size: 12,
              weight: "500",
            },
          },
        },
      },
    }),
    [pointCount]
  );

  return (
    <div className={`orders-charts ${hasCategory ? "orders-charts--split-layout" : ""}`}>
      {/* Optional: Orders by Type (only when API sends categoryCounts) */}
      {hasCategory && doughnutData && (
        <div className="dashboard-trend-panel orders-charts-split-card">
          <div className="orders-charts-doughnut-wrap">
            <Chart
              type="doughnut"
              data={doughnutData}
              options={doughnutOptions}
              aria-label="Orders by type distribution"
              role="img"
            />
          </div>
        </div>
      )}

      <div className="dashboard-trend-panel orders-charts-trend-panel">
        <header className="dashboard-trend-header dashboard-trend-header--toolbar">
          <h2 className="dashboard-trend-title">Orders Trend</h2>
          <p className="dashboard-trend-sub">{trendSubtitle}</p>
          <label className="dashboard-trend-select-wrap">
            <span className="visually-hidden">Date range</span>
            <select
              className="dashboard-trend-select"
              value={trendRange}
              onChange={(e) => onTrendRangeChange(e.target.value)}
              aria-label="Orders trend period"
            >
              {trendRangeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {trendRange === "custom" && (
            <>
              <label className="dashboard-trend-date-field">
                <span>From</span>
                <input
                  type="date"
                  value={customFromDate}
                  onChange={(e) => onCustomFromDateChange(e.target.value)}
                />
              </label>
              <label className="dashboard-trend-date-field">
                <span>To</span>
                <input
                  type="date"
                  value={customToDate}
                  min={customFromDate}
                  onChange={(e) => onCustomToDateChange(e.target.value)}
                />
              </label>
            </>
          )}
          <button
            type="button"
            className="dashboard-trend-link"
            onClick={onViewOrders}
          >
            View Orders →
          </button>
        </header>

        <div
          className={`orders-charts-bar-wrap ${pointCount > 7 ? "orders-charts-bar-wrap--dense" : ""} ${trendLoading ? "orders-charts-bar-wrap--loading" : ""}`}
        >
          {trendLoading && (
            <div className="orders-charts-bar-loading" aria-live="polite">
              <span className="orders-charts-bar-spinner" />
              <span>Updating chart…</span>
            </div>
          )}
          <Chart
            key={chartRefreshKey}
            type="bar"
            data={barData}
            options={barOptions}
            aria-label="Orders trend over time"
            role="img"
          />
        </div>
      </div>
    </div>
  );
}
